import { cn } from '@lemoment/lib/utils';
import { toast } from 'sonner';

import {
  LEAVE_TYPE_LABELS_KO,
  formatLeaveRange,
  todayIso,
} from '@lemoment/shared';
import type { Leave } from '@lemoment/shared';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Button,
} from '@lemoment/ui-kit';

import { useCancelLeave } from '../../leaves';

interface LeaveListItemProps {
  leave: Leave;
  onEdit: (leave: Leave) => void;
}

export const LeaveListItem = ({ leave, onEdit }: LeaveListItemProps) => {
  const cancelLeave = useCancelLeave();
  const showActions = leave.status === 'active' && leave.endDate >= todayIso();

  const handleCancel = () => {
    cancelLeave.mutate(leave.id, {
      onSuccess: () => toast.success('취소했습니다.'),
      onError: () => toast.error('취소에 실패했습니다.'),
    });
  };

  const rangeLabel = formatLeaveRange(leave.startDate, leave.endDate);
  const typeLabel = LEAVE_TYPE_LABELS_KO[leave.type];

  return (
    <div
      className={cn(
        'lift rounded-xl border border-border p-4 transition-colors',
        showActions ? 'bg-card hover:border-primary/30' : 'bg-muted/40',
      )}
    >
      <div className="flex items-center gap-3">
        <span
          className={cn(
            'h-2 w-2 shrink-0 rounded-full',
            showActions ? 'bg-primary' : 'bg-muted-foreground/40',
          )}
          aria-hidden
        />
        <span className="sr-only">{showActions ? '예정' : '지난'} 근태</span>
        <p className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-1.5 gap-y-0.5 text-sm text-muted-foreground">
          <span className="font-display text-[0.95rem] font-semibold tracking-tight text-foreground">
            {rangeLabel}
          </span>
          <span className="text-border">·</span>
          <span>{typeLabel}</span>
          {leave.type === 'annual' ? (
            <>
              <span className="text-border">·</span>
              <span>
                <span className="font-numeral">{leave.days}</span>일
              </span>
            </>
          ) : null}
          {leave.reason ? (
            <>
              <span className="text-border">·</span>
              <span>{leave.reason}</span>
            </>
          ) : null}
        </p>
        {showActions && (
          <div className="flex shrink-0 gap-2">
            <Button variant="outline" size="sm" onClick={() => onEdit(leave)}>
              수정
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={cancelLeave.isPending}
                  className="text-destructive hover:text-destructive"
                >
                  취소
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>근태를 취소하시겠습니까?</AlertDialogTitle>
                  <AlertDialogDescription>
                    {rangeLabel} · {typeLabel}
                    {leave.reason ? ` · ${leave.reason}` : ''} 일정이 삭제되고
                    캘린더에서도 제거됩니다. 되돌릴 수 없습니다.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>닫기</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleCancel}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    근태 취소
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}
      </div>
    </div>
  );
};
